import { format, isSameDay, isSameMinute } from 'date-fns'

/**
 * Formats a date range from start to end.
 *
 * @param {Date} start - The start date of the range.
 * @param {Date} end - The end date of the range.
 * @returns {string} A formatted string representing the date range.
 */
export function formatDateRange(start: Date, end: Date): string {
  // Check if start and end dates are valid
  if (!(start instanceof Date) || !(end instanceof Date)) {
    throw new Error('Invalid date range')
  }

  // Check if the start and end dates are the same
  if (isSameDay(start, end)) {
    // Check if the start and end times are the same
    if (isSameMinute(start, end)) {
      return format(start, 'MMMM dd, yyyy - HH:mm')
    } else {
      return format(start, 'MMMM dd, yyyy HH:mm').concat(` - ${format(end, 'HH:mm')}`)
    }
  }

  return `${format(start, 'MMMM d, yyyy HH:mm')} - ${format(end, 'MMMM d, yyyy HH:mm')}`
}

export function timeFromNow(inputDate: Date | string | number): string {
  const date = new Date(inputDate)
  const now = new Date()

  const diffMs = date.getTime() - now.getTime() // future = positive, past = negative
  const diffSec = Math.round(diffMs / 1000)
  const absSec = Math.abs(diffSec)

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

  if (absSec < 60) {
    return rtf.format(Math.round(diffSec), 'second')
  }

  const diffMin = Math.round(diffSec / 60)
  if (Math.abs(diffMin) < 60) {
    return rtf.format(diffMin, 'minute')
  }

  const diffHr = Math.round(diffSec / 3600)
  if (Math.abs(diffHr) < 24) {
    return rtf.format(diffHr, 'hour')
  }

  const diffDay = Math.round(diffSec / 86400)
  if (Math.abs(diffDay) < 30) {
    return rtf.format(diffDay, 'day')
  }

  const diffMonth = Math.round(diffSec / (30 * 86400))
  if (Math.abs(diffMonth) < 12) {
    return rtf.format(diffMonth, 'month')
  }

  const diffYear = Math.round(diffSec / (365 * 86400))
  return rtf.format(diffYear, 'year')
}
